import createBehavior from '@js/functions/createBehavior.js';
import Flickity from 'flickity';

const carouselBehavior = createBehavior(
  'carousel',
  {
    bindUI() {
      this.list = this.getChild('list');
      this.items = this.getChildren('item');
      this.btns = this.getChildren('btn');
    },
    getItemsWidth() {
      return Array.from(this.items).reduce((sum, item) => sum + item.offsetWidth, 0);
    },
    initFlickity() {

      const totalWidth = this.getItemsWidth();
      const viewportWidth = this.node.offsetWidth;
      const scrollable = totalWidth > viewportWidth;
      const wrapAround = scrollable && !this.node.dataset.carouselNoWrap;

      // hasControlsHidden uses watchCSS + CSS layout swap — keep it separate from has-no-wrap
      const hasControlsHidden = !!this.list.dataset.carouselMobileOnly ||
        ( this.node.dataset && this.node.dataset.carouselCheckControls && this.items.length <= 4 );

      const self = this;

      this.flky = new Flickity(this.list, {
        cellAlign: this.list.dataset.carouselAlign,
        watchCSS: hasControlsHidden,
        draggable: scrollable,
        contain: !wrapAround,
        wrapAround: wrapAround,
        imagesLoaded: true,
        on: {
          ready: () => {
            setTimeout(() => {
              self.updateA11y();
            }, 0);
            self.fixCaptionHeights();
          },
          change: (index) => {
            document.dispatchEvent(new CustomEvent('carousel:change'));
            self.updateA11y(true);
          },
          settle: () => {
            self.updateA11y();
            if (self.pendingTabTargetDirection) {
              self.pendingTabTargetDirection = null;
              // Flickity can dispatch `settle` one frame before the final
              // transform is reflected in layout. Read the slide geometry on
              // the next frame so repeated navigation cannot leave an edge
              // slide tabbable when it is no longer displayed.
              requestAnimationFrame(() => {
                setTimeout(() => self.updateA11y(), 100);
              });
            }
          },
          resize: () => {
            document.dispatchEvent(new CustomEvent('carousel:change'));
            self.fixCaptionHeights();
            self.updateA11y();
          },
        }
      });

      // Trick to prevent click on drag
      this.flky.on('dragStart', () => this.flky.slider.style.pointerEvents = 'none' );
      this.flky.on('dragEnd', () => { this.flky.slider.style.pointerEvents = ''; });

      // On mobile, Flickity intercepts touch events on non-selected cells (they register on
      // .flickity-viewport instead of the cell). Re-dispatch a custom event directly on the
      // behavior node inside the cell — events bubble UP, so dispatching on cellElement would
      // never reach child nodes listening with addEventListener.
      this.flky.on('staticClick', (event, pointer, cellElement) => {
        if (!cellElement || cellElement.classList.contains('is-selected')) return;
        const behaviorNode = cellElement.querySelector('[data-behavior]');
        if (behaviorNode) {
          behaviorNode.dispatchEvent(new CustomEvent('carousel:static-click'));
        }
      });

      // If check_controls valid hide controls
      if ( hasControlsHidden ) {
        this.node.classList.add('has-controls-hidden');
      }
      if ( !scrollable ) {
        this.node.classList.add('has-no-wrap');
      }
    },
    getVisibleSlides() {
      const viewportRect = this.flky.viewport.getBoundingClientRect();

      return this.flky.cells
        .map(cell => cell.element)
        .map(slide => ({
          slide,
          rect: slide.getBoundingClientRect(),
        }))
        // Treat a slide as visible only when its centre is inside the viewport.
        // Flickity can leave a narrow edge sliver of the next slide visible; that
        // sliver should not become the next keyboard target.
        .filter(({ rect }) => {
          const center = rect.left + (rect.width / 2);
          return center > viewportRect.left && center < viewportRect.right;
        })
        .sort((a, b) => a.rect.right - b.rect.right)
        .map(({ slide }) => slide);
    },
    getAdjacentSlide(direction) {
      const visibleSlides = this.getVisibleSlides();
      if (!visibleSlides.length) return null;

      const edgeSlide = direction === 'prev'
        ? visibleSlides[0]
        : visibleSlides[visibleSlides.length - 1];
      const edgeIndex = this.flky.cells.findIndex(cell => cell.element === edgeSlide);
      const offset = direction === 'prev' ? -1 : 1;
      const targetIndex = (edgeIndex + offset + this.flky.cells.length) % this.flky.cells.length;

      return this.flky.cells[targetIndex].element;
    },
    updateA11y() {
      if (!this.flky) return;

      const slides = this.flky.cells.map(cell => cell.element);
      const visibleSlides = new Set(this.getVisibleSlides());

      slides.forEach(slide => {
        const inView = visibleSlides.has(slide);
        const interactiveElements = slide.querySelectorAll('a, button, [tabindex]');

        // aria-hidden for screen readers, tabindex for keyboard nav
        // — never use inert: it blocks pointer-events and breaks hover/click
        slide.setAttribute('aria-hidden', inView ? 'false' : 'true');
        slide.removeAttribute('inert');

        // The slide wrapper is not itself interactive; only its actionable content
        // should participate in keyboard navigation.
        slide.removeAttribute('tabindex');
        interactiveElements.forEach(el => {
          el.setAttribute('tabindex', inView ? '0' : '-1');
        });
      });

      this.list.setAttribute('tabindex', '-1');
    },
    resetNavigationTabOrder(targetSlide = null) {
      if (!this.flky) return;

      const visibleSlides = new Set(this.getVisibleSlides());
      if (targetSlide) visibleSlides.add(targetSlide);

      this.flky.cells.forEach(cell => {
        const slide = cell.element;
        const inView = visibleSlides.has(slide);
        slide.querySelectorAll('a, button, [tabindex]').forEach(el => {
          el.setAttribute('tabindex', inView ? '0' : '-1');
        });
      });

      this.navigationTabTargetLocked = false;
      this.pendingTabTarget = null;
      this.pendingTabTargetDirection = null;
    },
    keyboardHandler(e) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          this.pendingTabTargetDirection = e.key === 'ArrowLeft' ? 'prev' : 'next';
          this.lastNavigationDirection = this.pendingTabTargetDirection;
          this.pendingTabTarget = this.getAdjacentSlide(this.pendingTabTargetDirection);
          this.navigationTabTargetLocked = true;

          if (e.key === 'ArrowLeft') {
              this.flky.previous();
          } else {
              this.flky.next();
          }
      }
    },
    fixCaptionHeights(){
      let maxHeight = 0;
      this.list.style.setProperty('--caption-height', '0px');
      this.list.querySelectorAll('.image__content').forEach((caption) => {
        const height = caption.offsetHeight;
        if (height > maxHeight) {
          maxHeight = height;
        }
      });
      this.list.style.setProperty('--caption-height', `${maxHeight}px`);
    },
    bindEvents() {
      Array.from(this.btns).map(btn => {
        btn.addEventListener('click', this.clickHandler, false);
        btn.addEventListener('keydown', this.navigationKeydownHandler, false);
      })

      if ( this.node.dataset ) {

        if ( this.node.dataset.carouselControlClick ) {
          Array.from(this.items).map(item => {
            item.addEventListener('click', this.clickItemHandler, false);
          })
        }

        if ( this.node.dataset.carouselForceResize ) {
          setTimeout( window.addEventListener('load', this.forceResize), 500 );
          setTimeout( window.addEventListener('load', this.forceResize), 1500 );
        }
      }

      this.list.addEventListener('keydown', this.keyboardHandler);
      this.node.addEventListener('focusout', this.navigationFocusOutHandler);

      document.addEventListener('card-video:selected', this.onCardVideoSelected);
    },
    navigationKeydownHandler(e) {
      if (e.key !== 'Tab' || e.shiftKey || !this.navigationTabTargetLocked || !this.pendingTabTarget) return;

      // The auditor requires the next Tab to enter the newly revealed slide,
      // but focus must remain on the control until the user actually presses
      // Tab. Hand the user-initiated Tab to that target without introducing a
      // competing positive-tabindex order.
      e.preventDefault();
      const targetSlide = this.pendingTabTarget;
      const target = targetSlide.querySelector('a, button, [tabindex]');
      if (!target) return;

      target.focus();
      this.resetNavigationTabOrder(targetSlide);
    },
    navigationFocusOutHandler(e) {
      if (!this.navigationTabTargetLocked) return;

      const nextFocusedElement = e.relatedTarget;
      if (!nextFocusedElement || !this.node.contains(nextFocusedElement)) {
        this.resetNavigationTabOrder();
      }
    },
    onCardVideoSelected(e) {
      this.flky.next( true );
    },
    clickHandler(e) {
      e.preventDefault();
      // Get direction.
      const direction = e.currentTarget.getAttribute('data-direction');
      this.pendingTabTargetDirection = direction;
      this.lastNavigationDirection = direction;
      this.pendingTabTarget = this.getAdjacentSlide(direction);
      this.navigationTabTargetLocked = true;

      // Based on direction.
      if (direction === 'prev') {
        this.flky.previous();
      } else if (direction === 'next') {
        this.flky.next();
      }
    },
    clickItemHandler(e) {

      if ( e.currentTarget.classList.contains('is-selected') ) return;

      e.preventDefault();
      e.stopPropagation();

      this.flky.select(e.currentTarget.dataset.id);
    },
    forceResize() {
      window.dispatchEvent(new Event('resize'));
    },
    unbindEvents() {
      Array.from(this.btns).map(btn => {
        btn.removeEventListener('click', this.clickHandler);
        btn.removeEventListener('keydown', this.navigationKeydownHandler);
      })

      if ( this.node.dataset ) {

        if ( this.node.dataset.carouselControlClick ) {
          Array.from(this.items).map(item => {
            item.removeEventListener('click', this.clickItemHandler);
          })
        }

        if ( this.node.dataset.carouselForceResize ) {
          window.removeEventListener('load', this.forceResize);
        }
      }

      this.list.removeEventListener('keydown', this.keyboardHandler);
      this.node.removeEventListener('focusout', this.navigationFocusOutHandler);
    }
  },
  {
    init() {
      this.flky = null;
      this.pendingTabTargetDirection = null;
      this.pendingTabTarget = null;
      this.navigationTabTargetLocked = false;
      this.lastNavigationDirection = null;

      this.bindUI();
      this.initFlickity();
      this.bindEvents();
    },
    destroy() {
      this.unbindEvents();
    }
  }
);

export default carouselBehavior;
